import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, ArrowUp, ArrowDown, Trash2, Star } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Project {
  id: string;
  title: string;
  location: string;
  status: string;
  project_images: { image_url: string; alt_text?: string }[];
}

interface FeaturedProject {
  id: string;
  project_id: string;
  display_order: number;
  is_active: boolean;
  city_override?: string;
  projects: Project;
}

export const FeaturedProjectsManager = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [featuredProjects, setFeaturedProjects] = useState<FeaturedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const { toast } = useToast();

  // Load projects and featured projects
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all projects
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id,
          title,
          location,
          status,
          project_images (
            image_url,
            alt_text
          )
        `)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;

      // Load featured projects
      const { data: featuredData, error: featuredError } = await supabase
        .from('featured_projects')
        .select(`
          *,
          projects (
            id,
            title,
            location,
            status,
            project_images (
              image_url,
              alt_text
            )
          )
        `)
        .eq('is_active', true)
        .order('display_order');

      if (featuredError) throw featuredError;

      setProjects(projectsData || []);
      setFeaturedProjects(featuredData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load projects data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addFeaturedProject = async () => {
    if (!selectedProjectId) return;

    try {
      const nextOrder = Math.max(...featuredProjects.map(fp => fp.display_order), 0) + 1;
      
      const { error } = await supabase
        .from('featured_projects')
        .insert({
          project_id: selectedProjectId,
          display_order: nextOrder,
          is_active: true
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project added to featured list",
      });

      setSelectedProjectId('');
      loadData();
    } catch (error) {
      console.error('Error adding featured project:', error);
      toast({
        title: "Error",
        description: "Failed to add featured project",
        variant: "destructive",
      });
    }
  };

  const removeFeaturedProject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('featured_projects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project removed from featured list",
      });

      loadData();
    } catch (error) {
      console.error('Error removing featured project:', error);
      toast({
        title: "Error",
        description: "Failed to remove featured project",
        variant: "destructive",
      });
    }
  };

  const moveProject = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = featuredProjects.findIndex(fp => fp.id === id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= featuredProjects.length) return;

    try {
      const current = featuredProjects[currentIndex];
      const target = featuredProjects[newIndex];

      // Swap display orders
      await Promise.all([
        supabase.from('featured_projects').update({ display_order: target.display_order }).eq('id', current.id),
        supabase.from('featured_projects').update({ display_order: current.display_order }).eq('id', target.id)
      ]);

      toast({
        title: "Success",
        description: "Project order updated",
      });

      loadData();
    } catch (error) {
      console.error('Error moving project:', error);
      toast({
        title: "Error",
        description: "Failed to update project order",
        variant: "destructive",
      });
    }
  };

  const availableProjects = projects.filter(
    project => !featuredProjects.some(fp => fp.project_id === project.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add New Featured Project */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Featured Project
          </CardTitle>
          <CardDescription>
            Select a project to feature in the "Recent Projects" section
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger className="flex-1 min-h-[44px]">
                <SelectValue placeholder="Select a project to feature..." />
              </SelectTrigger>
              <SelectContent>
                {availableProjects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    <div className="flex items-center gap-2">
                      <span>{project.title}</span>
                      <Badge variant="secondary" className="text-xs">
                        {project.location}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={addFeaturedProject} disabled={!selectedProjectId} className="min-h-[44px] w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current Featured Projects */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            Featured Projects ({featuredProjects.length}/3)
          </CardTitle>
          <CardDescription>
            These projects appear in the "Recent Projects" section. Drag to reorder.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {featuredProjects.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No featured projects yet. Add some projects above.
            </div>
          ) : (
            <div className="space-y-4">
              {featuredProjects.map((featured, index) => (
                <div key={featured.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 border rounded-lg">
                  <div className="flex sm:flex-col gap-2 sm:gap-1 order-2 sm:order-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveProject(featured.id, 'up')}
                      disabled={index === 0}
                      className="min-h-[36px] min-w-[36px] flex-1 sm:flex-none"
                    >
                      <ArrowUp className="h-4 w-4" />
                      <span className="ml-1 sm:hidden">Up</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveProject(featured.id, 'down')}
                      disabled={index === featuredProjects.length - 1}
                      className="min-h-[36px] min-w-[36px] flex-1 sm:flex-none"
                    >
                      <ArrowDown className="h-4 w-4" />
                      <span className="ml-1 sm:hidden">Down</span>
                    </Button>
                  </div>

                  <div className="flex items-center gap-4 flex-1 order-1 sm:order-2 w-full sm:w-auto">
                    {featured.projects.project_images.length > 0 && (
                      <img
                        src={featured.projects.project_images[0].image_url}
                        alt={featured.projects.project_images[0].alt_text || featured.projects.title}
                        className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold truncate">{featured.projects.title}</h4>
                      <p className="text-sm text-muted-foreground truncate">{featured.projects.location}</p>
                      <Badge variant="outline" className="text-xs mt-1">
                        Order: {featured.display_order}
                      </Badge>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeFeaturedProject(featured.id)}
                    className="min-h-[36px] order-3 w-full sm:w-auto"
                  >
                    <Trash2 className="h-4 w-4 mr-2 sm:mr-0" />
                    <span className="sm:hidden">Remove</span>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>
            This is how the featured projects will appear on your website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredProjects.slice(0, 3).map((featured) => (
              <div key={featured.id} className="border rounded-lg overflow-hidden">
                {featured.projects.project_images.length > 0 && (
                  <img
                    src={featured.projects.project_images[0].image_url}
                    alt={featured.projects.project_images[0].alt_text || featured.projects.title}
                    className="w-full h-32 object-cover"
                  />
                )}
                <div className="p-3">
                  <h5 className="font-semibold text-sm">{featured.projects.title}</h5>
                  <p className="text-xs text-muted-foreground">{featured.projects.location}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};